/* eslint-disable max-classes-per-file */

import { Mutex } from "async-mutex";
import { action, computed, observable } from "mobx";
import { create, persist } from "mobx-persist";
import { pushRoute } from "../components/app/router/history";
import { debug } from "../components/util/debug";
import { INITIAL_ROUTE, TOKEN_EXCHANGE_THRESHOLD_MS } from "../config";
import { t } from "../i18n/util";
import { API, STATUS_CODE_BAD_REQUEST, STATUS_CODE_NOT_ALLOWED, STATUS_CODE_UNAUTHORIZED } from "../network/API";
import { generalStore } from "./GeneralStore";
import { pollingManager } from "./PollingManager";
import { APIClientStatusCodeError } from "../network/NetworkStapler";

export type Credentials = {
    access_token: string;
    refresh_token: string;
    expires_in: number;
};

export type AuthError = "PasswordWrong" | "Unknown" | "NotAllowed";

const mutex = new Mutex();

class Auth {
    @persist("object") @observable _credentials: Credentials | null = null;
    @persist @observable username = "";
    @observable error: AuthError | null = null;
    @observable isLoading = false;
    @observable isRehydrated = false;
    tokenTime: number | null = null;
    @persist @observable pictureUrl?: string;
    @persist @observable givenName?: string;

    @action logout() {
        this.wipe(null);
    }

    @persist @observable redirectAfterLogin?: string;

    @computed get isAuthenticated() {
        return !!this.credentials;
    }

    get credentials() {
        return this._credentials;
    }

    set credentials(credentials: Credentials | null) {
        this._credentials = credentials;
    }

    getGoogleLoginUrl = async () => {
        if (this.isLoading) {
            // bailout, noop
            return;
        }

        this.isLoading = true;

        try {
            const response = await API.getGoogleLogin();
            this.error = null;
            return response.url;
        } catch (error) {
            if (
                error instanceof APIClientStatusCodeError &&
                (error.statusCode === STATUS_CODE_UNAUTHORIZED || error.statusCode === STATUS_CODE_BAD_REQUEST)
            ) {
                this.wipe("PasswordWrong");
            } else {
                this.wipe("Unknown");
            }
        } finally {
            this.isLoading = false;
        }
    };

    @action googleLogin = async (state: string, code: string) => {
        if (this.isLoading) {
            // bailout, noop
            return;
        }

        this.isLoading = true;

        try {
            const credentials = await API.loginWithGoogle(state, code);
            this.credentials = credentials;

            this.error = null;
        } catch (error) {
            console.error(error);
            if (error instanceof APIClientStatusCodeError) {
                if (error.statusCode === STATUS_CODE_NOT_ALLOWED && error.response.type === "USER_DEACTIVATED") {
                    this.wipe("NotAllowed");
                } else if (
                    error.statusCode === STATUS_CODE_UNAUTHORIZED ||
                    error.statusCode === STATUS_CODE_BAD_REQUEST
                ) {
                    this.wipe("PasswordWrong");
                } else {
                    this.wipe("Unknown");
                }
            } else {
                this.wipe("Unknown");
            }
        } finally {
            this.isLoading = false;
        }
    };

    @action tokenExchange = async () => {
        this.isLoading = true;
        let success = false;
        try {
            if (this.credentials === null) {
                throw new Error("No valid credentials are available");
            }

            const res = await API.refresh(this.credentials.refresh_token);

            const date = new Date();
            this.tokenTime = date.getTime();

            this.credentials = res;

            this.error = null;

            success = true;
        } catch (e) {
            generalStore.setError(t("error.login"), e);
            this.wipe("Unknown");
        } finally {
            this.isLoading = false;
        }

        return success;
    };

    handleTokenExchange = async () => {
        return await mutex.runExclusive(async () => {
            if (!this.tokenTime || !this.credentials) {
                return this.tokenExchange();
            }

            // Check if refresh should be done. This is needed because if e.g. 5 calls are done simultaneously on
            // a page change then all 5 would trigger a token refresh. This check makes sure only
            // the first call goes through, all subsequent calls would not be done because we prevent more than one refresh every
            // TOKEN_EXCHANGE_THRESHOLD_MS.
            // ATTENTION: If you expire the token manually on the BE side this would fail in the first TOKEN_EXCHANGE_THRESHOLD_MS, only then the token would be refreshed.
            const date = new Date();
            const shouldRefresh = this.tokenTime + TOKEN_EXCHANGE_THRESHOLD_MS < date.getTime();
            if (shouldRefresh) {
                return this.tokenExchange();
            }

            return true;
        });
    };

    // Returns true if token exchange was successful otherwise wipe store
    @action handleUnauthorized = async () => {
        let tokenExchangeSuccess = false;
        if (this.credentials) {
            try {
                tokenExchangeSuccess = await this.handleTokenExchange();
            } catch {
                this.wipe(null);
            }
        } else {
            this.wipe(null);
        }

        return tokenExchangeSuccess;
    };

    @action private wipe(error: AuthError | null) {
        this.credentials = null;
        this.error = error;
        this.isLoading = false;
        this.pictureUrl = undefined;
        this.givenName = undefined;

        pollingManager.stopPolling();
    }

    async refreshAfterLogin() {
        if (this.redirectAfterLogin) {
            // Redirect to requested target
            pushRoute(this.redirectAfterLogin);
            this.redirectAfterLogin = undefined;
        } else {
            // Default route after login
            pushRoute(INITIAL_ROUTE);
        }
    }
}

const hydrate = create({
    storage: require("localforage")
});

const authStore = new Auth();

hydrate("auth", authStore)
    .then(() => {
        // trigger token exchange if credentials are available...
        if (authStore.credentials !== null) {
            debug.log("hydrate.auth: credentials are available, awaiting new token...");
            authStore
                .tokenExchange()
                .then(() => {
                    debug.log("hydrate.auth: received new token!");
                    authStore.isRehydrated = true;
                })
                .catch(() => {
                    debug.log("hydrate.auth: failed to receive new token!");
                    authStore.isRehydrated = true;
                });
        } else {
            authStore.isRehydrated = true;
            debug.log("rehydrated, no credentials are available.");
        }
    })
    .catch((error) => {
        console.error(error);
    });

export { authStore };
